import React, {useState, useEffect} from "react"
import {useParams, useHistory} from "react-router-dom"
import {useSelector} from "react-redux"
import styled from "@emotion/styled"
import {BiArrowBack, BiCheckCircle, BiChevronUp, BiChevronDown} from "react-icons/bi"
import {Button, Input} from "reactstrap"
import {useMutation} from "@tanstack/react-query"
import {
  getEtrackerQuestionnaire,
  editPolicyETrackerMilestoneQuestion
} from "services/milestoneServices"
import MilestoneStageDropdown from "./E-TrackerStageDropdown"
import ScoringGuide from "./ScoringGuide"
import Sidebar from "components/Common/UploadEvidence/UploadEvidenceSidebar"
import ConfirmModal from "components/Common/ConfirmModal"
import {milestoneOrFocalAreaAccess} from "helpers/access/milestoneOrFocalAreaAccess"

const PageContent = styled.div`
    background-color: #f8f9fa;
    min-height: 100vh;
    padding: 2em;
    padding-top: 5em;

    & h2 {
        margin-top: 25px;
        margin-bottom: 20px;
        color: #049046;
        font-weight: 400;
        font-size: 20px;
    }
`

const ProgressBarContainer = styled.div`
    width: 80%;
    height: 15px;
    background-color: #e0e0e0;
    border-radius: 10px;
    margin: 20px auto;
    overflow: hidden;
`

const ProgressBarFill = styled.div`
    height: 100%;
    background-color: ${({progress}) =>
            progress <= 50 ? "#ff4e4e" : progress <= 75 ? "#ff9900" : "#198754"};
    width: ${({progress}) => `${progress}%`};
    transition: width 0.4s ease, background-color 0.4s ease;
`

const QuestionBlock = styled.div`
    background-color: white;
    padding: 1.5em;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;
    position: relative;

    & p {
        font-size: 1.1rem;
        font-weight: 400;
        margin-top: 20px;
        margin-bottom: 20px;
    }
`;

const RequiredTag = styled.span`
    position: absolute;
    top: 10px;
    left: 10px;
    background-color: #f0f0f0;
    color: #888;
    font-size: 0.8rem;
    padding: 2px 8px;
    border-radius: 4px;
    font-weight: bold;
    margin-bottom: 10px;
`

const AnswerOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const AnswerButton = styled.button`
    background-color: ${(props) => (props.selected ? "#d4f8d4" : "#f0f0f0")};
    color: ${(props) => (props.selected ? "#198754" : "#333")};
    border: 1px solid ${(props) => (props.selected ? "#198754" : "#e0e0e0")};
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 5px;
    width: 100%;

    &:hover {
        background-color: ${(props) => (props.disabled ? "#f0f0f0" : "#d4f8d4")};
    }

    & svg {
        visibility: ${(props) => (props.selected ? "visible" : "hidden")};
    }
`;

function FillFormEtracker({alert}) {
  const {policyId, stageId} = useParams()
  const {user} = useSelector((state) => state.Profile)
  const currentUserId = user?.id

  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const toggleConfirm = () => setIsConfirmOpen(!isConfirmOpen)
  const [formDataState, setFormDataState] = useState(null)
  const [responses, setResponses] = useState([])
  const [progress, setProgress] = useState(0)
  const [selectedStageID, setSelectedStageID] = useState(parseInt(stageId))
  const [expandedMilestone, setExpandedMilestone] = useState(null)
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const [selectedQuestionId, setSelectedQuestionId] = useState(null)
  const [milestoneDetails, setMilestoneDetails] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  const history = useHistory()

  const toggleSidebar = (questionId) => {
    setSelectedQuestionId(questionId)
    setSidebarOpen((prev) => !prev)
  }

  const getActionText = ({
                           isOwnerAndSupervisorSame,
                           ownerId,
                           supervisorId,
                           currentUserId,
                           ownerCanUpdate,
                           supervisorCanUpdate
                         }) => {
    if (isOwnerAndSupervisorSame && currentUserId === ownerId && currentUserId === supervisorId) {
      return "Submit Responses"
    } else if (ownerCanUpdate) {
      return "Submit Responses for Supervisor Approval"
    } else if (supervisorCanUpdate) {
      return "Approve Submitted Responses"
    }
    return null
  }

  useEffect(() => {
    const loadFormDetails = async () => {
      setIsLoading(true)
      try {
        const data = await getEtrackerQuestionnaire(policyId, selectedStageID)

        let milestones = data.policy_e_tracker_milestones.map((milestone) => {
          const ownerId = milestone?.owner_and_supervisor?.owner_id
          const supervisorId = milestone?.owner_and_supervisor?.supervisor_id
          const isOwnerAndSupervisorSame = ownerId === supervisorId

          const milestone_questions = milestone.milestone_questions.map(question => ({
            ...question,
            evidenceData: question?.evidence_files?.map(evidence => ({
              // id: evidence.id,
              evidence_name: evidence.evidence_name,
              evidence_file: evidence.evidence_file,
              _delete: false // Flag for deletion
            })) || []
          }))
          console.log("milestone_questions", milestone_questions)

          return {
            ...milestone,
            milestone_questions,
            ownerId,
            supervisorId,
            isComplete: milestone?.is_complete,
            approvedBySupervisor: milestone?.approved_by_supervisor,
            isOwnerAndSupervisorSame
          }
        })

        let milestonesAccess = milestones.map((milestone) =>
          milestoneOrFocalAreaAccess({currentUserId, ...milestone})
        )

        // Update milestones with access and actionText
        milestones = milestones.map((milestone, index) => {
          const ownerCanUpdate = milestonesAccess[index]?.canOwnerUpdate
          const supervisorCanUpdate = milestonesAccess[index]?.canSupervisorUpdate

          const actionText = getActionText({
            isOwnerAndSupervisorSame: milestone?.isOwnerAndSupervisorSame,
            ownerId: milestone?.ownerId,
            supervisorId: milestone?.supervisorId,
            currentUserId,
            ownerCanUpdate,
            supervisorCanUpdate
          })

          return {
            ...milestone,
            access: milestonesAccess[index],
            actionText
          }
        })

        data.policy_e_tracker_milestones = milestones

        setFormDataState(data)

        const questions = data.policy_e_tracker_milestones.flatMap((milestone) =>
          milestone.milestone_questions.map((question) => ({
            milestone_id: milestone.milestone_id,
            question_id: question.question_id,
            question_response: question.question_response,
            question_note: question.question_note,
            // Evidence is now part of formStateData
          })) || []
        )

        setResponses(questions)


        if (data.policy_e_tracker_milestones.length > 0) {
          const milestone = data.policy_e_tracker_milestones[0]
          const ownerId = milestone.owner_and_supervisor?.owner_id
          const supervisorId = milestone.owner_and_supervisor?.supervisor_id

          setMilestoneDetails({
            ownerId,
            supervisorId,
            isComplete: milestone.is_complete,
            approvedBySupervisor: milestone.approved_by_supervisor
          })
        }
      } catch (error) {
        console.error("Error fetching form data:", error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFormDetails()
  }, [policyId, selectedStageID, expandedMilestone])

  useEffect(() => {
    const totalQuestions = responses.length
    const answeredQuestions = responses.filter((res) => res.question_response).length
    setProgress(Math.floor((answeredQuestions / totalQuestions) * 100))
  }, [responses])


  const handleResponseChange = (question_id, answer) => {
    const score = parseInt(answer.split("]")[0].replace("[", ""), 10) || 0;

    setResponses((prevResponses) =>
      prevResponses.map((res) =>
        res.question_id === question_id
          ? {
            ...res,
            question_response: answer.split("]")[1].trim(),
            question_response_score: score,
          }
          : res
      )
    );
  };

  const handleSave = () => {
    saveResponses()
  }

  const onAddFile = (fileData) => {
    setFormDataState(prev => {
      const updated = {...prev};
      updated.policy_e_tracker_milestones = updated.policy_e_tracker_milestones.map(milestone => {
        return {
          ...milestone,
          milestone_questions: milestone.milestone_questions.map(question => {
            if (question.question_id === selectedQuestionId) {
              return {
                ...question,
                evidenceData: [...(question.evidenceData || []), fileData]
              }
            }
            return question
          })
        }
      })
      return updated
    });
  };

  // Update the onFileRemove function
  const onFileRemove = (index) => {
    setFormDataState(prev => {
      const updated = {...prev};
      updated.policy_e_tracker_milestones = updated.policy_e_tracker_milestones.map(milestone => {
        return {
          ...milestone,
          milestone_questions: milestone.milestone_questions.map(question => {
            if (question.question_id === selectedQuestionId) {
              const updatedEvidence = [...question.evidenceData];
              // Check if this is an existing evidence (has an ID) and mark for deletion if needed
              if (updatedEvidence[index]?.id) {
                updatedEvidence[index]._delete = true;
              } else {
                // If it's a new file, just remove it
                updatedEvidence.splice(index, 1);
              }
              return {
                ...question,
                evidenceData: updatedEvidence
              }
            }
            return question
          })
        }
      })
      return updated
    });
  };

  // Update the saveResponses mutation to use formStateData
  const {mutate: saveResponses} = useMutation({
    mutationFn: async () => {
      const validResponses = responses.filter(response => {
      const milestone = formDataState.policy_e_tracker_milestones.find(
        m => m.milestone_id === response.milestone_id
      );
      return milestone && !milestone.access?.forbiddenUpdate;
    });

      for (const response of validResponses) {
        const milestone = formDataState.policy_e_tracker_milestones.find(
          m => m.milestone_id === response.milestone_id
        )?.milestone_questions.find(
          q => q.question_id === response.question_id
        );

        if (!milestone || milestone.access?.forbiddenUpdate) {
          continue;
        }

        if (response.question_response) {
          const formData = new FormData();
          const questionEvidences = milestone?.evidenceData || [];

          // Prepare evidence data
          const evidenceUpdateRequests = questionEvidences
            .filter(evidence => !evidence._delete)
            .map(evidence => ({
              evidence_name: evidence.name,
              evidence_file: evidence.file
              // id: evidence.id || null
            }));

          formData.append('milestone_update_request_body', JSON.stringify({
            question_update_request_body: {
              question_response: response.question_response,
              ...(response.question_note && {question_note: response.question_note}),
            },
            question_evidence_update_request_bodies: evidenceUpdateRequests,
            question_evidence_ids_to_delete: questionEvidences
              .filter(evidence => evidence._delete && evidence.id)
              .map(evidence => evidence.id)
          }));

          // Add all files for this question
          questionEvidences.forEach(evidence => {
            if (evidence.file) {
              formData.append('evidence_files', evidence.file);
            }
          });

          await editPolicyETrackerMilestoneQuestion(
            policyId,
            selectedStageID,
            response.milestone_id,
            response.question_id,
            formData
          )
        }
      }
    },
    onSuccess: () => {
      alert("All responses saved successfully!", true)
    },
    onError: (error) => {
      alert("Failed to save responses. Please try again.", false)
      console.error("Error saving responses:", error)
    }
  })

  const handleMilestoneClick = (milestoneId) => {
    // Toggle the milestone's expanded state
    setExpandedMilestone((prev) =>
      prev === milestoneId ? null : milestoneId
    )
  }

  return (
    <PageContent>
      <ProgressBarContainer>
        <ProgressBarFill progress={progress}/>
      </ProgressBarContainer>

      <ScoringGuide/>

      <div className="country-selection space-x-5">
        <span className="mr-2 text-nowrap">Select Stage: </span>
        <MilestoneStageDropdown selectedStageID={selectedStageID} setMilestoneStage={setSelectedStageID}/>
      </div>

      <h2 className="text-green-700 text-2xl my-3">
        {formDataState?.policy_name || "e-Tracker Form"}
      </h2>

      {formDataState?.policy_e_tracker_milestones?.map((milestone) => (
        <div key={milestone.milestone_id} className="mb-3 border rounded-md bg-white">
          <div
            onClick={() => handleMilestoneClick(milestone.milestone_id)}
            className="flex justify-between items-center cursor-pointer p-3"
          >
            <div>
              <h3 className="text-lg">{milestone?.milestone_name}</h3>
              <div className="my-2">
                <p>
                  <strong>Owner:</strong> {milestone.owner_and_supervisor.owner.first_name}{" "}
                  {milestone.owner_and_supervisor.owner.last_name}</p>
              </div>
              <div className="my-2">
                <p>
                  <strong>Supervisor:</strong> {milestone.owner_and_supervisor.supervisor.first_name}{" "}
                  {milestone.owner_and_supervisor.supervisor.last_name}
                </p>
              </div>
            </div>
            <span className="text-3xl">
              {expandedMilestone === milestone.milestone_id ? <BiChevronUp/> : <BiChevronDown/>}
            </span>
          </div>

          {/* Expanded Section */}
          {expandedMilestone === milestone.milestone_id && (
            <div className="p-3">
              {/* Update Forbidden Alert */}
              {!isLoading && milestone.access.forbiddenUpdate && (
                <div className="alert alert-danger" role="alert" style={{textAlign: "center", marginTop: "20px"}}>
                  You do not have permission to edit this milestone.
                </div>
              )}

              {/* Milestone Questions */}
              {milestone?.milestone_questions.map((question, idx) => (
                <div key={question.question_id} className="p-3">
                  <QuestionBlock>
                    <RequiredTag>Required</RequiredTag>
                    <p>{`${idx + 1}. ${question?.e_tracker_milestone_question.question}`}</p>
                    <AnswerOptions>
                      {["[1]Yes", "[0]No"].map((option) => (
                        <AnswerButton
                          key={option}
                          selected={responses.find((res) => res.question_id === question.question_id)?.question_response === option.split("]")[1].trim()}
                          onClick={() => handleResponseChange(question.question_id, option)}
                          disabled={milestone?.access?.forbiddenUpdate}
                          style={{
                            cursor: milestone?.access?.forbiddenUpdate ? "not-allowed" : "pointer",
                            opacity: milestone?.access?.forbiddenUpdate ? 0.6 : 1,
                          }}
                        >
                          <BiCheckCircle/>
                          {option}
                        </AnswerButton>
                      ))
                      }
                    </AnswerOptions>
                    <Button
                      color="primary"
                      onClick={() => toggleSidebar(question.question_id)}
                      style={{marginTop: "15px"}}
                      disabled={milestone?.access?.forbiddenUpdate}
                    >
                      View/Upload Evidence
                    </Button>

                    <Input
                      type="textarea"
                      placeholder="Add a note..."
                      value={question.question_note || ""}
                      onChange={(e) =>
                        setResponses((prevResponses) =>
                          prevResponses.map((res) =>
                            res.question_id === question.question_id
                              ? {...res, question_note: e.target.value}
                              : res
                          )
                        )
                      }
                      disabled={milestone?.access?.forbiddenUpdate}
                      style={{
                        marginTop: "10px",
                        cursor: milestone?.access?.forbiddenUpdate ? "not-allowed" : "pointer",
                        opacity: milestone?.access?.forbiddenUpdate ? 0.6 : 1
                      }}
                    />
                  </QuestionBlock>

                  {/* Confirmation Modal */}
                  <ConfirmModal onConfirmClick={handleSave} show={isConfirmOpen} toggle={toggleConfirm}
                                role={milestone?.access?.role}/>

                  <Sidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    evidenceData={
                      formDataState?.policy_e_tracker_milestones
                        ?.flatMap(m => m.milestone_questions)
                        ?.find(q => q.question_id === selectedQuestionId)?.evidenceData || []
                    }
                    onAddFile={onAddFile}
                    onFileRemove={onFileRemove}
                    formType={'e_tracker'}
                    updateForbidden={
                      formDataState?.policy_e_tracker_milestones
                        ?.find(m => m.milestone_questions.some(q => q.question_id === selectedQuestionId))
                        ?.access?.forbiddenUpdate
                    }
                  />

                </div>
              ))}

              {/* Action Buttons (Go Back & Submit/Approve) */}
              <div className="flex justify-between mt-3">
                <Button className="common-button" onClick={() => history.goBack()}>
                  <BiArrowBack style={{marginRight: "4px"}}/> Go Back
                </Button>

                {milestone.actionText && (
                  <Button className="common-button" onClick={toggleConfirm}>
                    {milestone.actionText}
                  </Button>
                )}
              </div>

            </div>
          )}
        </div>
      ))}

    </PageContent>
  )

}

export default FillFormEtracker